import {
  type CustomerServiceAddressChangesPayload,
  GeocodeSourceSchema,
  type ICustomer,
} from "@mytour/shared";
import {z} from "zod";

export const customerFormSchema = z
  .object({
    customerType: z.enum(["individual", "professional"]),

    // Identity
    gender: z.enum(["", "mr", "mrs"]),
    firstName: z.string(),
    familyName: z.string(),
    companyLegalName: z.string(),
    siret: z.string(),
    phone: z.string(),
    email: z.string().email("Merci de saisir une adresse email valide").or(z.string().length(0)),

    // Service address
    serviceAddress: z.string(),
    serviceAddress2: z.string(),
    serviceZip: z.string(),
    serviceCity: z.string(),

    // Invoice address
    hasInvoiceAddress: z.boolean(),
    address: z.string(),
    address2: z.string(),
    zip: z
      .string()
      .refine(
        (v) => !v || /^[0-9]{5}$/.test(v),
        "Le code postal en France est une suite de cinq chiffres"
      ),
    city: z.string(),

    /**
     * Trio pin manuel (#929) : valeurs programmatiques posées par `setValue`
     * depuis `AddressGeocodeStatus`, jamais rendues par un champ — pas de
     * `.default()` (doctrine anti-`preserve-on-omit`, #507) : `undefined` =
     * "jamais touché", distinct de `null` = "retiré explicitement". Voir
     * `buildCustomerEntity.ts` pour la construction présence-based du payload.
     */
    serviceLat: z.number().nullable().optional(),
    serviceLong: z.number().nullable().optional(),
    serviceGeocodeSource: GeocodeSourceSchema.nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.customerType === "individual") {
      if (!data.familyName.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["familyName"],
          message: "Le nom est requis",
        });
      }
      if (!data.firstName.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["firstName"],
          message: "Le prénom est requis",
        });
      }
    } else {
      if (!data.companyLegalName.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["companyLegalName"],
          message: "La raison sociale est requise",
        });
      }
      if (!data.siret.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["siret"],
          message: "Le SIRET est requis",
        });
      } else if (!/^[0-9]{14}$/.test(data.siret.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["siret"],
          message: "Le SIRET doit contenir exactement 14 chiffres",
        });
      }
    }
  });

export type CustomerFormData = z.infer<typeof customerFormSchema> & {
  serviceAddressChanges?: CustomerServiceAddressChangesPayload;
};

export const EMPTY_DEFAULTS: CustomerFormData = {
  customerType: "individual",
  gender: "",
  firstName: "",
  familyName: "",
  companyLegalName: "",
  siret: "",
  phone: "",
  email: "",
  serviceAddress: "",
  serviceAddress2: "",
  serviceZip: "",
  serviceCity: "",
  hasInvoiceAddress: false,
  address: "",
  address2: "",
  zip: "",
  city: "",
  // serviceLat/serviceLong/serviceGeocodeSource omis exprès : `undefined`
  // (jamais posé par `.default()`, doctrine #507) = "aucune opinion".
};

export function customerToFormData(customer: ICustomer): Partial<CustomerFormData> {
  const type = customer.companyLegalName || customer.siret ? "professional" : "individual";
  return {
    customerType: type,
    gender: customer.gender ?? "",
    firstName: customer.firstName ?? "",
    familyName: customer.familyName ?? "",
    companyLegalName: customer.companyLegalName ?? "",
    siret: customer.siret ?? "",
    phone: customer.phone ?? "",
    email: customer.email ?? "",
    serviceAddress: customer.serviceAddress ?? "",
    serviceAddress2: customer.serviceAddress2 ?? "",
    // Strip whitespace from legacy zips imported with stray spaces ("75 008")
    // so existing valid-but-untidy data passes the strict /^[0-9]{5}$/ regex.
    serviceZip: stripZipWhitespace(customer.serviceZip ?? ""),
    serviceCity: customer.serviceCity ?? "",
    hasInvoiceAddress: !!(customer.address || customer.address2 || customer.zip || customer.city),
    address: customer.address ?? "",
    address2: customer.address2 ?? "",
    zip: stripZipWhitespace(customer.zip ?? ""),
    city: customer.city ?? "",
    /**
     * serviceLat/serviceLong/serviceGeocodeSource jamais seedés ici (#929) :
     * le badge "Point placé manuellement" est piloté directement par la prop
     * `initialManualPin` de `CustomerForm` (`getCustomerPosition(customer)`),
     * pas par ce form state — seeder ces 3 clés ferait resoumettre le trio à
     * CHAQUE save d'un client déjà `manual`, même non retouché, cassant la
     * distinction présence-based que `buildCustomerEntity` doit préserver.
     */
  };
}

/**
 * Strip every whitespace character from a zip value. Used both at load time
 * (to repair legacy data with stray spaces) and at input time (to keep
 * typos like "75 008" from tripping the strict regex).
 */
export function stripZipWhitespace(value: string): string {
  return value.replace(/\s+/g, "");
}
